import React from "react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";

const techniques = [
  {
    n: "01", name: "Gravure", image: "/techniques/gravure.jpg",
    desc: "Graver un prénom, une date ou un mot tendre dans le bois ou le métal, c'est inscrire durablement un souvenir que l'on peut toucher du bout des doigts.",
    examples: ["Bracelet personnalisé avec le prénom du résident", "Plaque souvenir gravée"],
  },
  {
    n: "02", name: "Couture", image: "/techniques/couture.jpg",
    desc: "Assembler des tissus doux et variés pour créer des objets réconfortants, à câliner et à manipuler, qui apaisent les sens et rassurent.",
    examples: ["Coussin sensoriel aux tissus doux", "Pochette de rangement personnalisée"],
  },
  {
    n: "03", name: "Broderie", image: "/techniques/broderie.jpg",
    desc: "Broder au fil, avec patience et délicatesse, un prénom ou un motif cher — un geste artisanal qui porte toute la tendresse d'une attention.",
    examples: ["Mouchoir brodé au prénom", "Petit tableau décoratif personnalisé"],
  },
  {
    n: "04", name: "Impression 3D", image: "/techniques/impression3d.jpg",
    desc: "Concevoir des objets aux formes douces et adaptées, pensés pour stimuler le toucher et accompagner les exercices, avec précision et bienveillance.",
    examples: ["Objet tactile stimulant", "Support adapté pour exercices cognitifs"],
  },
  {
    n: "05", name: "Flocage", image: "/techniques/flocage.jpg",
    desc: "Imprimer un message doux ou une illustration chère sur le tissu, pour transformer un vêtement du quotidien en objet chaleureux et personnel.",
    examples: ["T-shirt personnalisé avec un message doux", "Tote bag souvenir"],
  },
  {
    n: "06", name: "Bougies", image: "/techniques/bougies.jpg",
    desc: "Coulées à la main et parfumées avec délicatesse, nos bougies diffusent une lumière douce et des senteurs familières qui apaisent, réconfortent et ravivent les souvenirs.",
    examples: ["Bougie personnalisée au parfum d'enfance", "Photophore avec le prénom du résident"],
  },
  {
    n: "07", name: "Résine", image: "/techniques/resine.jpg",
    desc: "Enfermer une fleur séchée, une photo ou un petit souvenir dans la résine, c'est figer un fragment d'histoire dans un objet limpide que l'on garde tout près de soi.",
    examples: ["Pendentif souvenir avec fleurs séchées", "Presse-papier contenant une photo de famille"],
  },
];

const NosTechniques = () => (
  <div className="pt-32">
    <section className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="text-sm uppercase tracking-[0.3em] text-primary mb-5">Notre savoir-faire</p>
        <h1 className="font-serif-display text-5xl sm:text-6xl font-light max-w-3xl leading-tight">Nos techniques artisanales</h1>
        <p className="mt-6 text-lg text-foreground/70 max-w-2xl leading-relaxed">
          Chaque objet naît de gestes patients et bienveillants. Découvrez les techniques que nous mettons au service
          du bien-être et de la mémoire, et quelques accessoires thérapeutiques qu'elles permettent de créer.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="mt-12 rounded-[2.5rem] overflow-hidden border border-warm/25 shadow-[0_20px_60px_rgb(0,0,0,0.08)] grain" data-testid="techniques-banner">
          <img src="/affiche-techniques.jpg" alt="Le plan de travail de l'atelier : broderie, gravure, fils et bois, veillés par une gardienne à la lanterne" className="w-full h-auto block" />
        </div>
      </Reveal>
    </section>

    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 space-y-20 lg:space-y-28">
      {techniques.map((t, i) => (
        <Reveal key={t.n}>
          <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
            <div className="relative">
              <div className="overflow-hidden rounded-[2rem] border border-border/50 bg-accent/20">
                <img src={t.image} alt={`Technique : ${t.name}`} className="w-full aspect-square object-cover hover:scale-[1.03] transition-transform duration-700" data-testid={`technique-image-${t.name.toLowerCase().replace(/[^a-z0-9]/g, "")}`} />
              </div>
              <span className="absolute -top-5 -left-3 font-serif-display text-7xl text-primary/20">{t.n}</span>
            </div>
            <div data-testid={`technique-${t.name.toLowerCase().replace(/[^a-z0-9]/g, "")}`}>
              <h2 className="font-serif-display text-4xl sm:text-5xl font-light">{t.name}</h2>
              <p className="mt-5 text-lg text-foreground/70 leading-relaxed">{t.desc}</p>
              <p className="mt-8 text-xs uppercase tracking-[0.25em] text-secondary-foreground/70">Exemples d'accessoires</p>
              <ul className="mt-4 space-y-3">
                {t.examples.map((ex, j) => (
                  <li key={j} className="flex items-start gap-3 bg-card rounded-2xl border border-border/50 px-5 py-4">
                    <span className="font-serif-display text-xl text-primary">✦</span>
                    <span className="text-foreground/80">{ex}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      ))}
    </section>

    <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-10 text-center">
      <Reveal>
        <h2 className="font-serif-display text-4xl sm:text-5xl font-light">Une idée d'objet en tête ?</h2>
        <p className="mt-5 text-foreground/70 max-w-xl mx-auto">Nous mêlons ces techniques pour créer la pièce juste, à partir de l'histoire de votre proche.</p>
        <Link to="/contact" data-testid="techniques-cta" className="inline-block mt-8 rounded-full bg-primary text-primary-foreground px-9 py-4 hover:scale-105 transition-transform duration-300">Faire une demande sur mesure</Link>
      </Reveal>
    </section>
  </div>
);

export default NosTechniques;
